import { unsafeCSS, html } from 'lit';
import { state, property, queryAll } from 'lit/decorators.js';
import { unsafeHTML } from 'lit/directives/unsafe-html.js';

import { PopBaseComponent } from '../../../base/pop-base-component.js';
import { Constants as ProductCenterTableWrapperConstants } from '../../product-center-table-wrapper/product-center-table-wrapper.constants.js';
import { Constants } from './price-table.constants.js';
import { customFormatDate } from '../../../common/utils/formatDate.js';
import { formatNumberToCurrency } from '../../../common/utils/formatNumber.js';
import {
  CurrentSort,
  Disclaimer,
  DisclaimerPositioning,
  Product,
  SortDirection,
  TableRecordVariant,
} from '../../product-center-table-wrapper/product-center-table-wrapper.model';
import { getCurrencySymbol } from '../../../common/utils/currency';
import { buildShareClassDescriptor } from '../../../common/utils/buildShareClass';
import { zustandStateToComponent } from '../../store/product-center.store';
import { TableTypeEnum } from '../../store/product-center.store.model';
import {
  sortDataCells,
  sortProductDataCells,
  validateIfProductHeaderSort,
} from '../common/helper/table-helper';
import { sanitizeHTML } from '../../../common/utils/sanitizeHTML';
import {
  checkProductCallback,
  getAPCDateFormat,
  getReportButtonAriaLabel,
  handleAdvancedSortByDropdownChange,
  handleSortProductDataCells,
  handleUpdate,
  setAnalyticsAttributesForBeaconIconButton,
  renderProductCellData,
  renderProductHeader,
  renderReportButton,
  renderTableCells,
  renderTableHeader,
  sortDataCellsCallBack,
} from '../../product-center-table-wrapper/product-center-table-wrapper.utils.js';
import Locale from '../../../common/utils/locale.js';

import style from '../common/css/table.scss?inline';

/**
 * @tag pop-pc-price-table-component
 * @property {ProductCenterExhibit} [componentObject] Price Table content
 * @attribute {ProductCenterExhibit} [data-component-object] PRice Table content
 */
export class PopPriceTableComponent extends PopBaseComponent {
  static get styles() {
    return [...super.styles, unsafeCSS(style)];
  }

  static readonly tagName = Constants.TAG_NAME;

  /**
   * @internal
   */
  @property()
  private filteredProducts: Product[] = [];

  /**
   * @internal
   */
  @property()
  private sortByDropdownValue =
    ProductCenterTableWrapperConstants.DEFAULT_SORT_BY_DROP_DOWN_OPTION.value;

  /**
   * @internal
   */
  @property()
  selectedProducts: Product['productCode'][] = [];

  @state()
  public sortDirectionState: SortDirection = SortDirection.ASCENDING;

  @property()
  tableHeaders: NodeListOf<Element> | undefined;

  /**
   * @internal
   */
  private readonly priceDisclaimers: Disclaimer[] = [];

  private currentSort?: CurrentSort;

  @queryAll('beacon-icon-button.report-button')
  beaconIconButtons!: NodeListOf<HTMLElement>;

  constructor() {
    super();
    this.enableI18n = true;
  }

  /*
   * Component Methods
   */

  firstUpdated() {
    this.beaconIconButtons.forEach((button) => {
      setAnalyticsAttributesForBeaconIconButton(button);
    });
  }

  update(changedProperties: Map<string, unknown>) {
    handleUpdate(this, changedProperties);
    super.update(changedProperties);
  }

  private getBaseCurrencyName(): string {
    const foundProduct = this.filteredProducts.find(
      (product) => product.productName && product.productName.baseCurrencyName,
    );

    return foundProduct?.productName?.baseCurrencyName || '';
  }

  private sortByDropdownChangeHandler(e: CustomEvent) {
    this.sortByDropdownValue = handleAdvancedSortByDropdownChange(
      e,
      ProductCenterTableWrapperConstants.DEFAULT_SORT_BY_DROP_DOWN_OPTION.value,
      this.tableHeaders,
      this.filteredProducts,
      ProductCenterTableWrapperConstants.SORT_BUTTON_ID,
      this.shadowRoot,
      this.requestUpdate.bind(this),
      (sort) => {
        this.currentSort = sort;
      },
      (products) => {
        this.filteredProducts = products;
      },
    );
    if (this.currentSort) {
      this.currentSort.columnId = undefined;
    }
  }

  private sortProductDataCells(e: CustomEvent) {
    handleSortProductDataCells(this, e);
  }

  private renderTableHead() {
    return html`
      <tr>
        ${renderProductHeader(
          {
            rowspan: 1,
            sortByDropdownValue: this.sortByDropdownValue,
            sortByCallBack: this.sortByDropdownChangeHandler,
            onSort: this.sortProductDataCells,
            tableType: TableTypeEnum.PRICE,
          },
          new Locale(this.language, this.country),
        )}
        ${renderTableHeader({
          columnId: Constants.PRICE_COLUMN_IDS.NAV_PRICE,
          headerName: `${this.localize(Constants.TRANSLATION_KEYS.NAV_PRICE, {
            baseCurrency: this.getBaseCurrencyName(),
          })}`,
          colspan: 1,
          rowspan: 2,
          parentHeader: null,
          sortable: true,
          customClass:
            ProductCenterTableWrapperConstants.CUSTOM_CLASSES.HEADER_ALIGNMENT,
          onSort: (e: CustomEvent) =>
            sortDataCellsCallBack(
              this,
              e,
              Constants.PRICE_COLUMN_IDS.NAV_PRICE,
            ),
        })}
        ${renderTableHeader({
          columnId: Constants.PRICE_COLUMN_IDS.APPLICATION_PRICE,
          headerName: `${this.localize(
            Constants.TRANSLATION_KEYS.APPLICATION_PRICE,
            {
              baseCurrency: this.getBaseCurrencyName(),
            },
          )}`,
          colspan: 1,
          rowspan: 2,
          parentHeader: null,
          sortable: true,
          customClass:
            ProductCenterTableWrapperConstants.CUSTOM_CLASSES.HEADER_ALIGNMENT,
          onSort: (e: CustomEvent) =>
            sortDataCellsCallBack(
              this,
              e,
              Constants.PRICE_COLUMN_IDS.APPLICATION_PRICE,
            ),
        })}
        ${renderTableHeader({
          columnId: Constants.PRICE_COLUMN_IDS.WITHDRAWAL_PRICE,
          headerName: `${this.localize(
            Constants.TRANSLATION_KEYS.WITHDRAWAL_PRICE,
            {
              baseCurrency: this.getBaseCurrencyName(),
            },
          )}`,
          colspan: 1,
          rowspan: 2,
          parentHeader: null,
          sortable: true,
          customClass:
            ProductCenterTableWrapperConstants.CUSTOM_CLASSES.HEADER_ALIGNMENT,
          onSort: (e: CustomEvent) =>
            sortDataCellsCallBack(
              this,
              e,
              Constants.PRICE_COLUMN_IDS.WITHDRAWAL_PRICE,
            ),
        })}
        ${renderTableHeader({
          columnId: Constants.PRICE_COLUMN_IDS.PRICE_AS_OF,
          headerName: this.localize(Constants.TRANSLATION_KEYS.PRICE_AS_OF),
          colspan: 1,
          rowspan: 2,
          parentHeader: null,
          sortable: true,
          customClass:
            ProductCenterTableWrapperConstants.CUSTOM_CLASSES.HEADER_ALIGNMENT,
          onSort: (e: CustomEvent) =>
            sortDataCellsCallBack(
              this,
              e,
              Constants.PRICE_COLUMN_IDS.PRICE_AS_OF,
            ),
        })}
        ${renderTableHeader({
          columnId: Constants.PRICE_COLUMN_IDS.PERFORMANCE_REPORT,
          headerName: this.localize(
            Constants.TRANSLATION_KEYS.PERFORMANCE_REPORT,
          ),
          colspan: 1,
          rowspan: 2,
          parentHeader: null,
          sortable: false,
          customClass:
            ProductCenterTableWrapperConstants.CUSTOM_CLASSES.HEADER_ALIGNMENT,
        })}
        ${renderTableHeader({
          columnId: Constants.PRICE_COLUMN_IDS.QUARTERLY_REVIEW,
          headerName: this.localize(
            Constants.TRANSLATION_KEYS.QUARTERLY_REVIEW,
          ),
          colspan: 1,
          rowspan: 2,
          parentHeader: null,
          sortable: false,
          customClass:
            ProductCenterTableWrapperConstants.CUSTOM_CLASSES.HEADER_ALIGNMENT,
        })}
      </tr>
    `;
  }

  private renderTableBody() {
    if (this.currentSort) {
      if (validateIfProductHeaderSort(this.currentSort.columnId)) {
        this.filteredProducts = sortProductDataCells(
          this.currentSort.tableHeaderEl,
          this.filteredProducts,
          this.currentSort.columnId,
        );
      } else {
        this.filteredProducts = sortDataCells(
          this.currentSort.tableHeaderEl,
          this.filteredProducts,
          this.currentSort.columnId,
        );
      }
    }

    return html`
      ${this.filteredProducts.map((product: Product) => {
        return html`
          <tr>
            ${renderProductCellData({
              index: JSON.stringify(product.productName),
              sortValue: product.productName[this.sortByDropdownValue] ?? '',
              checkboxLabel: product.productName.vehicleTypeName,
              fundName: product.productName.fundName,
              shareClass: product.productName.shareClass
                ? buildShareClassDescriptor(product.productName.shareClass)
                : '',
              currencySymbol: getCurrencySymbol(
                product.productName.baseCurrencyName,
              ),
              currencyName: product.productName.baseCurrencyName,
              apir: product.productName.apir ?? '',
              productCode: product.productCode,
              checkProductCallBack: (event: Event) =>
                checkProductCallback(event, TableTypeEnum.PRICE),
              isChecked: this.selectedProducts.includes(product.productCode),
              fundLink: product.productName.fundLink,
              showPopCheckbox: true,
              disclaimers: product.disclaimers ?? undefined,
              apirLabel: this.localize(
                ProductCenterTableWrapperConstants.TRANSLATION_KEYS.APIR,
              ),
              noticeLabel: this.localize(
                ProductCenterTableWrapperConstants.TRANSLATION_KEYS.NOTICE,
              ),
            })}
            ${renderTableCells({
              sortValue: `${product.pricing?.navPrice}`,
              variant: TableRecordVariant.NUMERAL,
              cellClass: '',
              value: html`${formatNumberToCurrency(
                product.pricing?.navPrice,
                product.productName.baseCurrencyName,
                ProductCenterTableWrapperConstants.CUSTOM_NO_VALUE_SYMBOL,
              )}`,
            })}
            ${renderTableCells({
              sortValue: `${product.pricing?.applicationPrice}`,
              variant: TableRecordVariant.NUMERAL,
              cellClass: '',
              value: html`${formatNumberToCurrency(
                product.pricing?.applicationPrice,
                product.productName.baseCurrencyName,
                ProductCenterTableWrapperConstants.CUSTOM_NO_VALUE_SYMBOL,
              )}`,
            })}
            ${renderTableCells({
              sortValue: `${product.pricing?.withdrawalPrice}`,
              variant: TableRecordVariant.NUMERAL,
              cellClass: '',
              value: html`${formatNumberToCurrency(
                product.pricing?.withdrawalPrice,
                product.productName.baseCurrencyName,
                ProductCenterTableWrapperConstants.CUSTOM_NO_VALUE_SYMBOL,
              )}`,
            })}
            ${renderTableCells({
              sortValue: `${+new Date(product.pricing?.priceAsOfDate || '')}`,
              variant: TableRecordVariant.NUMERAL,
              cellClass: '',
              value: product.pricing?.priceAsOfDate
                ? html`${getAPCDateFormat(
                    customFormatDate(
                      product.pricing?.priceAsOfDate,
                      ProductCenterTableWrapperConstants.DATE_FORMATS
                        .MEDIUM_DATE_FORMAT,
                      undefined,
                      ProductCenterTableWrapperConstants.CUSTOM_NO_VALUE_SYMBOL,
                    ),
                  )}`
                : html`${ProductCenterTableWrapperConstants.CUSTOM_NO_VALUE_SYMBOL}`,
            })}
            ${renderTableCells({
              sortValue: undefined,
              variant: TableRecordVariant.TEXT,
              cellClass: Constants.REPORT_ROW_CLASS,
              value: renderReportButton(
                product.pricing?.performanceReportURL,
                getReportButtonAriaLabel(
                  product,
                  Constants.TRANSLATION_KEYS.PERFORMANCE_REPORT,
                ),
                Constants.TRANSLATION_KEYS.PERFORMANCE_REPORT,
              ),
            })}
            ${renderTableCells({
              sortValue: undefined,
              variant: TableRecordVariant.TEXT,
              cellClass: Constants.REPORT_ROW_CLASS,
              value: renderReportButton(
                product.pricing?.quarterlyReportURL,
                getReportButtonAriaLabel(
                  product,
                  Constants.TRANSLATION_KEYS.QUARTERLY_REVIEW,
                ),
                Constants.TRANSLATION_KEYS.QUARTERLY_REVIEW,
              ),
            })}
          </tr>
        `;
      })}
    `;
  }

  renderComponent() {
    return html` <beacon-table
      defer-until="visible"
      variant="fixed"
      density="standard"
    >
      <table>
        <thead>
          ${this.renderTableHead()}
        </thead>
        <tbody>
          ${this.renderTableBody()}
        </tbody>
      </table>
      <div class="disclaimer">
        ${this.priceDisclaimers.map((disclaimer: Disclaimer) =>
          unsafeHTML(sanitizeHTML(disclaimer.disclaimerText.html)),
        )}
      </div>
    </beacon-table>`;
  }

  render() {
    if (!this.filteredProducts && !this.componentObject) {
      return html``;
    }
    return this.renderComponent();
  }
}

customElements.define(
  Constants.TAG_NAME,
  zustandStateToComponent(
    {
      mapState: (productCenterState) => {
        return {
          filteredProducts: productCenterState.getFilteredProducts(),
          selectedProducts:
            productCenterState.selectedProducts[TableTypeEnum.PRICE],
          priceDisclaimers: productCenterState.getDisclaimersByPositioning(
            DisclaimerPositioning.PRICE,
          ),
        };
      },
    },
    PopPriceTableComponent,
  ),
);
